import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api, onAuthLost, setAccessToken } from "@/lib/api";
import type { LoggedInUser, LoginRequest, LoginResponse } from "@/types/api";

interface AuthState {
  user: LoggedInUser | null;
  loading: boolean;
  login: (input: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  hasPerm: (perm: string) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<LoggedInUser | null>(null);
  const [loading, setLoading] = useState(true);

  const login = useCallback(async (input: LoginRequest) => {
    const { data } = await api.post<LoginResponse>(
      "/api/v1/auth/login",
      input,
    );
    setAccessToken(data.access);
    setUser(data.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/api/v1/auth/logout");
    } catch {
      // ignore
    }
    setAccessToken(null);
    setUser(null);
  }, []);

  // Try silent refresh on mount (cookie still valid → restore session)
  useEffect(() => {
    onAuthLost(() => {
      setAccessToken(null);
      setUser(null);
    });
    (async () => {
      try {
        const r = await api.post<{ access: string }>(
          "/api/v1/auth/refresh",
          {},
        );
        setAccessToken(r.data.access);
        const me = await api.get<LoggedInUser>("/api/v1/auth/me");
        setUser(me.data);
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const hasPerm = useCallback(
    (perm: string) => user?.permissions.includes(perm) ?? false,
    [user],
  );

  const value = useMemo<AuthState>(
    () => ({ user, loading, login, logout, hasPerm }),
    [user, loading, login, logout, hasPerm],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
